import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

interface TopBarSubtitleContextValue {
  subtitleOverride: string | null;
  setSubtitleOverride: (subtitle: string | null) => void;
}

const TopBarSubtitleContext = createContext<TopBarSubtitleContextValue | null>(
  null,
);

export function TopBarSubtitleProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [subtitleOverride, setSubtitleOverride] = useState<string | null>(null);

  useEffect(() => {
    setSubtitleOverride(null);
  }, [pathname]);

  const value = useMemo(
    () => ({
      subtitleOverride,
      setSubtitleOverride,
    }),
    [subtitleOverride],
  );

  return (
    <TopBarSubtitleContext.Provider value={value}>
      {children}
    </TopBarSubtitleContext.Provider>
  );
}

function useTopBarSubtitleContext(): TopBarSubtitleContextValue {
  const ctx = useContext(TopBarSubtitleContext);
  if (!ctx) {
    throw new Error(
      "useTopBarSubtitle must be used within TopBarSubtitleProvider",
    );
  }
  return ctx;
}

/** Optional read for TopBar — returns null when provider is absent. */
export function useTopBarSubtitleOverride(): string | null {
  return useContext(TopBarSubtitleContext)?.subtitleOverride ?? null;
}

/**
 * Override the short label under the Twido logo for the current page.
 * Clears automatically on unmount or when `subtitle` becomes empty.
 */
export function useTopBarSubtitle(subtitle: string | null | undefined) {
  const { setSubtitleOverride } = useTopBarSubtitleContext();

  useEffect(() => {
    const trimmed = subtitle?.trim();
    setSubtitleOverride(trimmed || null);
    return () => setSubtitleOverride(null);
  }, [subtitle, setSubtitleOverride]);
}
